/**
 * Pure duel-round outcome calculator shared by `useVsRobotDuel` and
 * `useVsHumanDuel`. Given a USER click and an OPPONENT response, returns
 * the gains for each side, the human-readable outcome banner, and which
 * sound to play (if any).
 *
 * Inputs:
 *  - u   : { time:number, correct:boolean } | null   user click (null = never clicked)
 *  - opp : { time:number, correct:boolean } | null   opponent answer (null = no response yet)
 *  - opts.simulMs (default 500)                      ±window classed as "simultaneous"
 *  - opts.allowOppNull (default false)               VS HUMAN accepts opp=null (timeout)
 *  - opts.oppName (default "Adversário")             template token for the banner
 *
 * Output:
 *  - { userGain, oppGain, outcomeText, outcomeSound }
 *    where outcomeSound ∈ { "correct", "wrong", null }
 *
 * Behaviour matrix (canonical — copied verbatim from the original inline
 * resolvers so tests catch any future drift):
 *   1. no-user + no-opp        → 0/0, "timeout"        (VS HUMAN only)
 *   2. no-user + opp correct   → 0/1, "wrong"
 *   3. no-user + opp wrong     → 2/0, "correct"        (asymmetric +2)
 *   4. user + no-opp correct   → 1/0, "correct"        (VS HUMAN only)
 *   5. user + no-opp wrong     → 0/2, "wrong"          (VS HUMAN only, asym +2)
 *   6. simultaneous            → both evaluated independently (+1 each on success)
 *   7. user-first correct      → 1/0, "correct"
 *   8. user-first wrong        → 0/2, "wrong"          (asymmetric +2)
 *   9. opp-first correct       → 0/1, "wrong"
 *  10. opp-first wrong         → 2/0, "correct"        (asymmetric +2)
 */
export function computeVsOutcome(u, opp, opts = {}) {
  const SIMUL_MS = opts.simulMs ?? 500;
  const oppName = opts.oppName ?? "Adversário";
  // VS ROBOT uses "para o robot" in one of the asymmetric banners while
  // VS HUMAN uses "para ele" — preserve both via an opt-in token.
  const oppPossessive = opts.oppPossessive ?? "ele";
  const allowOppNull = !!opts.allowOppNull;

  // ---- Edge cases first (VS HUMAN only) ---------------------------------
  if (!u && !opp) {
    return { userGain: 0, oppGain: 0, outcomeText: "Tempo esgotado — ninguém respondeu.", outcomeSound: null };
  }
  if (allowOppNull && u && !opp) {
    if (u.correct) {
      return { userGain: 1, oppGain: 0, outcomeText: `${oppName} demorou demais — +1 para ti.`, outcomeSound: "correct" };
    }
    return { userGain: 0, oppGain: 2, outcomeText: `Erraste, ${oppName} não respondeu — +2 para ele.`, outcomeSound: "wrong" };
  }

  // ---- Core matrix ------------------------------------------------------
  if (!u) {
    // VS ROBOT: user didn't respond before the robot's deadline.
    if (opp.correct) {
      return { userGain: 0, oppGain: 1, outcomeText: `Demoraste demais! ${oppName} responde e ganha +1.`, outcomeSound: "wrong" };
    }
    return { userGain: 2, oppGain: 0, outcomeText: `${oppName} respondeu primeiro e errou — +2 para ti!`, outcomeSound: "correct" };
  }

  if (Math.abs(opp.time - u.time) <= SIMUL_MS) {
    const userGain = u.correct ? 1 : 0;
    const oppGain = opp.correct ? 1 : 0;
    let outcomeText = "";
    let outcomeSound = null;
    if (u.correct && opp.correct) outcomeText = "Empate certo! Ambos marcam +1.";
    else if (u.correct && !opp.correct) { outcomeText = `Acertaste em simultâneo, ${oppName} errou — só tu marcas.`; outcomeSound = "correct"; }
    else if (!u.correct && opp.correct) { outcomeText = `${oppName} acertou em simultâneo — só ele marca.`; outcomeSound = "wrong"; }
    else outcomeText = "Ambos erraram em simultâneo. Próxima!";
    return { userGain, oppGain, outcomeText, outcomeSound };
  }

  if (u.time < opp.time) {
    // User-first asymmetric.
    if (u.correct) return { userGain: 1, oppGain: 0, outcomeText: "Acertaste primeiro! +1 para ti.", outcomeSound: "correct" };
    return { userGain: 0, oppGain: 2, outcomeText: `Erraste primeiro! ${oppName} ganha +2.`, outcomeSound: "wrong" };
  }

  // Opponent-first asymmetric.
  if (opp.correct) return { userGain: 0, oppGain: 1, outcomeText: `${oppName} acertou primeiro! +1 para ${oppPossessive}.`, outcomeSound: "wrong" };
  return { userGain: 2, oppGain: 0, outcomeText: `${oppName} errou primeiro! +2 para ti.`, outcomeSound: "correct" };
}
